import { useEffect, useState } from "react";
import { api } from "./api";
import "./question-discussion.css";

type Comment = {
  id: string;
  parentId: string | null;
  body: string;
  author?: string;
  authorName?: string;
  created?: string;
  likes?: number;
  liked?: boolean;
  mine?: boolean;
  unavailable?: boolean;
  replies?: Comment[];
};
type Topic = {
  question: string;
  locked: boolean;
  title?: string;
  company?: string;
  count?: number;
};
type Props = {
  question?: string;
  requireLogin: () => boolean;
  onError: (message: string) => void;
  notify?: (message: string) => void;
  onCountChange?: (count: number) => void;
  onSelect?: (question: string) => void;
};
export function QuestionDiscussion({
  question,
  requireLogin,
  onError,
  notify,
  onCountChange,
  onSelect,
}: Props) {
  const [selected, setSelected] = useState(question || "");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [prompt, setPrompt] = useState<{ source: string; body: string } | null>(
    null,
  );
  const [count, setCount] = useState(0);
  const [problem, setProblem] = useState<{
    company: string;
    title: string;
    context: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [failure, setFailure] = useState("");
  const [body, setBody] = useState("");
  const [parent, setParent] = useState<Comment | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const [report, setReport] = useState<Comment | null>(null);
  const [reason, setReason] = useState("");
  useEffect(() => {
    setSelected(question || "");
  }, [question]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setLocked(false);
    setFailure("");
    setComments([]);
    setProblem(null);
    setPrompt(null);
    const path = selected
      ? `/questions/${encodeURIComponent(selected)}/comments`
      : "/question-discussions";
    api(path)
      .then((data) => {
        if (!active) return;
        if (selected) {
          setComments(data.comments);
          setProblem(data.question);
          setPrompt(data.prompt || null);
          setCount(data.count);
          onCountChange?.(data.count);
        } else setTopics(data);
      })
      .catch((e: Error) => {
        if (active) {
          setLocked(e.message.includes("풀이 후"));
          setFailure(e.message);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selected, revision]);
  useEffect(() => {
    setParent(null);
    setBody("");
    setReport(null);
    setExpanded([]);
  }, [selected]);
  const base = `/questions/${encodeURIComponent(selected)}/comments`;
  async function act(task: () => Promise<unknown>) {
    if (busy || !requireLogin()) return;
    setBusy(true);
    try {
      await task();
      setRevision((v) => v + 1);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function renderComment(c: Comment, reply = false) {
    return (
      <article
        className={`question-comment ${reply ? "is-reply" : ""}`}
        key={c.id}
      >
        {!c.unavailable && (
          <header>
            <strong>{c.authorName}</strong>
            <time>
              {c.created
                ? new Date(c.created).toLocaleString("ko-KR", {
                    month: "numeric",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : ""}
            </time>
          </header>
        )}
        <p className={c.unavailable ? "muted" : ""}>{c.body}</p>
        {!c.unavailable && (
          <div className="question-comment-actions">
            <button
              disabled={busy}
              aria-pressed={c.liked}
              onClick={() =>
                act(() => api(`${base}/${encodeURIComponent(c.id)}/like`, {}))
              }
            >
              좋아요 {c.likes || 0}
            </button>
            {!reply && (
              <button
                onClick={() => {
                  if (requireLogin()) setParent(c);
                }}
              >
                답글
              </button>
            )}
            {c.mine ? (
              <button
                disabled={busy}
                onClick={() =>
                  act(() =>
                    api(
                      `${base}/${encodeURIComponent(c.id)}`,
                      undefined,
                      "DELETE",
                    ),
                  )
                }
              >
                삭제
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    if (requireLogin()) {
                      setReport(c);
                      setReason("");
                    }
                  }}
                >
                  신고
                </button>
                <button
                  disabled={busy}
                  onClick={() =>
                    act(async () => {
                      await api("/blocks", { user: c.author });
                      notify?.("작성자를 차단했어요.");
                    })
                  }
                >
                  작성자 차단
                </button>
              </>
            )}
          </div>
        )}
        {!!c.replies?.length && (
          <>
            <button
              className="question-replies-toggle"
              aria-expanded={expanded.includes(c.id)}
              onClick={() =>
                setExpanded((v) =>
                  v.includes(c.id)
                    ? v.filter((id) => id !== c.id)
                    : [...v, c.id],
                )
              }
            >
              답글 {c.replies.length}개{" "}
              {expanded.includes(c.id) ? "접기" : "보기"}
            </button>
            {expanded.includes(c.id) &&
              c.replies.map((r) => renderComment(r, true))}
          </>
        )}
      </article>
    );
  }
  return (
    <section className="question-discussion">
      <div className="section-heading">
        <h3>
          {selected ? "이 문제의 투자 이야기" : "문제 토론"}
          {selected && !loading && !locked && !failure ? ` · ${count}` : ""}
        </h3>
        {selected && !question && (
          <button onClick={() => setSelected("")}>목록으로</button>
        )}
      </div>
      {loading ? (
        <p className="muted">이야기를 불러오는 중이에요.</p>
      ) : failure ? (
        <p role="status">{failure}</p>
      ) : !selected ? (
        <div className="question-topics">
          {topics.length ? (
            topics.map((t) => (
              <button
                key={t.question}
                disabled={t.locked}
                onClick={() => {
                  if (onSelect) onSelect(t.question);
                  else setSelected(t.question);
                }}
              >
                <strong>
                  {t.locked ? "풀이 후 확인할 수 있어요" : t.company || t.title}
                </strong>
                {!t.locked && <span>댓글 {t.count}개 · 이야기 보기</span>}
              </button>
            ))
          ) : (
            <p className="muted">
              아직 투자 이야기가 없어요. 문제를 풀고 첫 댓글을 남겨 보세요.
            </p>
          )}
        </div>
      ) : (
        <>
          {problem && (
            <details className="original-question">
              <summary>{problem.company} · 문제 다시 보기</summary>
              <h4>{problem.title}</h4>
              <p>{problem.context}</p>
            </details>
          )}
          {prompt && (
            <aside className="thought-prompt">
              <h4>{prompt.source} · 생각할 거리</h4>
              <p>{prompt.body}</p>
            </aside>
          )}
          <div>
            {comments.length ? (
              comments.map((c) => renderComment(c))
            ) : (
              <p className="muted">
                어떤 생각으로 선택했나요? 첫 이야기를 남겨 주세요.
              </p>
            )}
          </div>
          <form
            className="question-comment-form"
            onSubmit={(e) => {
              e.preventDefault();
              act(async () => {
                await api(base, { body, parentId: parent?.id });
                setBody("");
                setParent(null);
                if (parent) setExpanded((v) => [...v, parent.id]);
              });
            }}
          >
            {parent && (
              <div>
                <strong>{parent.authorName}님에게 답글</strong>
                <button type="button" onClick={() => setParent(null)}>
                  취소
                </button>
              </div>
            )}
            <label htmlFor={`discussion-body-${selected}`}>
              {parent ? "답글 남기기" : "내 생각 남기기"}
            </label>
            <textarea
              id={`discussion-body-${selected}`}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={1000}
              placeholder="이 선택에 대한 생각을 나눠 주세요."
              rows={3}
              disabled={busy}
            />
            <div>
              <small>{body.length}/1,000</small>
              <button className="btn primary" disabled={busy || !body.trim()}>
                {busy ? "등록 중…" : parent ? "답글 등록" : "댓글 등록"}
              </button>
            </div>
          </form>
          {report && (
            <form
              className="question-comment-form"
              onSubmit={(e) => {
                e.preventDefault();
                act(async () => {
                  await api(`${base}/${encodeURIComponent(report.id)}/report`, {
                    reason,
                  });
                  setReport(null);
                  notify?.("신고가 접수되었어요.");
                });
              }}
            >
              <label htmlFor="question-report-reason">신고 사유</label>
              <textarea
                id="question-report-reason"
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
              />
              <div>
                <button type="button" onClick={() => setReport(null)}>
                  취소
                </button>
                <button disabled={busy || !reason.trim()}>신고 접수</button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}
